import axios, { type AxiosRequestConfig, type GenericAbortSignal, type InternalAxiosRequestConfig } from 'axios'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import router from '@dashboard/router'
import { useSystemStore } from '@dashboard/stores/systemStore'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipProgress?: boolean
    /** Marks the logout request, which `withoutOtherRequests()` lets through while it holds the rest. */
    duringSignOut?: boolean
  }
}

// Track whether we've successfully authenticated in this session.
// When false, 401s are handled by the router auth middleware (which
// redirects to login with the intended URL preserved). When true,
// a 401 means the session expired mid-use and we need to force logout.
let hasEstablishedSession = false

// Callback to clear auth store state on session expiry.
// Set by authStore to avoid circular dependency (axiosApi ↔ authStore).
let onSessionExpired: (() => void) | null = null

export function registerSessionExpiredCallback(callback: () => void) {
  onSessionExpired = callback
}

export function markSessionEstablished() {
  hasEstablishedSession = true
}

export function clearSessionFlag() {
  hasEstablishedSession = false
}

export function isSessionEstablished() {
  return hasEstablishedSession
}

// A response carries the cookie of the session its request was sent with, and the browser
// stores it even after the page has navigated away. One that arrived after the logout signed
// the browser back in, so signing out aborts every request in flight.
const inFlight = new Map<GenericAbortSignal, AbortController>()
let signingOut: Promise<void> | null = null

/**
 * Aborts this page's requests in flight and holds new ones until `signOut` settles. Mark the
 * logout request itself `duringSignOut`.
 */
export async function withoutOtherRequests<T>(signOut: () => Promise<T>): Promise<T> {
  let release = () => {}
  signingOut = new Promise((resolve) => {
    release = resolve
  })
  for (const controller of inFlight.values()) {
    controller.abort()
  }
  try {
    return await signOut()
  } finally {
    signingOut = null
    release()
  }
}

const beginRequest = async (config: InternalAxiosRequestConfig): Promise<void> => {
  if (signingOut && !config.duringSignOut) {
    await signingOut
  }
  const controller = new AbortController()
  const callerSignal = config.signal
  if (callerSignal?.aborted) {
    controller.abort()
  }
  callerSignal?.addEventListener?.('abort', () => controller.abort())
  config.signal = controller.signal
  inFlight.set(controller.signal, controller)
}

const settleRequest = (config?: AxiosRequestConfig) => {
  if (config?.signal) {
    inFlight.delete(config.signal)
  }
}

export const noAuthApi = axios.create({
  baseURL: '',
  withCredentials: false,
  withXSRFToken: false,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})

const api = axios.create({
  baseURL: '',
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})

noAuthApi.interceptors.request.use(async (config) => {
  await beginRequest(config)
  NProgress.start()
  return config
})

noAuthApi.interceptors.response.use(
  (response) => {
    settleRequest(response.config)
    NProgress.done()
    return response
  },
  (error) => {
    settleRequest(error.config)
    NProgress.done()
    return Promise.reject(error)
  }
)

const tracksProgress = (config?: AxiosRequestConfig) => config?.skipProgress !== true

api.interceptors.request.use(async (config) => {
  await beginRequest(config)
  if (tracksProgress(config)) {
    NProgress.start()
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    settleRequest(response.config)
    if (tracksProgress(response.config)) {
      NProgress.done()
    }
    return response
  },
  (error) => {
    settleRequest(error.config)
    if (tracksProgress(error.config)) {
      NProgress.done()
    }

    if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
      const systemStore = useSystemStore()
      systemStore.setMaintenance(true, error.response.data.message, error.response.data.estimatedEnd)
    }

    // Check for 401 Unauthorized error
    if (error.response?.status === 401) {
      const publicRoutes = ['login', 'register', 'forgot-password', 'reset-password', 'agency-signup']
      const currentRouteName = router.currentRoute?.value?.name as string | undefined

      if (currentRouteName && !publicRoutes.includes(currentRouteName)) {
        if (hasEstablishedSession) {
          // Session expired mid-use. Clear local state and redirect to login.
          // IMPORTANT: Do NOT call api.post('/logout') here — the session is
          // already invalid server-side. Hitting /logout would destroy the
          // shared session cookie (.rightmessage.com), which kills sessions
          // on other subdomains too (e.g., Filament admin panel).
          hasEstablishedSession = false
          onSessionExpired?.()
          void router.push({ name: 'login' })
        }
        // Otherwise: initial page load or admin team switch where session
        // isn't established yet. Let the router auth middleware handle it —
        // it redirects to login with the intended URL preserved.
      }
    }

    return Promise.reject(error)
  }
)

export default api
